const FREE_GIFT_KEY = "_free_gift";
const FREE_GIFT_CODE = "_code_id";
const FREE_GIFT_VALUE = "1";
const CART_UPDATE_EVENT = "cart-update";
const DISMISSED_PREFIX = "gift-rules:dismissed:";

/**
 * @typedef {Object} SectionRenderTarget
 * @property {string} id
 * @property {string} section
 * @property {string} [selector]
 *
 * @typedef {Object} CartDiscountCode
 * @property {string} code
 * @property {boolean} applicable
 *
 * @typedef {Object} CartLine
 * @property {number} id
 * @property {number} variant_id
 * @property {number} quantity
 * @property {number} line
 * @property {string} [key]
 * @property {number} final_line_price
 * @property {Record<string, string> | null} properties
 *
 * @typedef {Object} CartJson
 * @property {CartLine[]} items
 * @property {CartDiscountCode[]} [discount_codes]
 * @property {number} items_subtotal_price
 *
 * @typedef {Object} GiftRule
 * @property {string} blockId
 * @property {number} variantId
 * @property {boolean} requiresDiscountCode
 * @property {string} codes
 * @property {string} matchMode
 * @property {number | null} thresholdCents
 * @property {number | null} startUnix
 * @property {number | null} endUnix
 */

/**
 * @returns {{ cart_add_url: string; cart_change_url: string } | null}
 */
function getCartRoutes() {
  const value =
    typeof window !== "undefined" &&
    window.routes &&
    typeof window.routes === "object"
      ? window.routes
      : null;

  if (!value || !value.cart_add_url || !value.cart_change_url) {
    return null;
  }

  return value;
}

/**
 * @param {string} type
 * @returns {{ method: string; headers: Record<string, string> }}
 */
function getFetchConfig(type) {
  if (typeof window.fetchConfig === "function") {
    return window.fetchConfig(type);
  }

  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`
    }
  };
}

/**
 * @returns {SectionRenderTarget[]}
 */
function getSectionTargets() {
  const elements = document.querySelectorAll("cart-drawer-items, cart-items");
  /** @type {SectionRenderTarget[]} */
  const targets = [];
  const seen = new Set();

  for (const el of elements) {
    if (
      !(el instanceof HTMLElement) ||
      typeof el.getSectionsToRender !== "function"
    ) {
      continue;
    }

    const fromElement = el.getSectionsToRender();
    if (!Array.isArray(fromElement)) {
      continue;
    }

    for (const target of fromElement) {
      if (!target || !target.section || !target.id) {
        continue;
      }
      const key = `${target.id}::${target.section}::${target.selector || ""}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      targets.push(target);
    }
  }

  return targets;
}

/**
 * @param {string} html
 * @param {string} selector
 * @returns {string}
 */
function getSectionInnerHTML(html, selector) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const selected = doc.querySelector(selector);
  return selected ? selected.innerHTML : "";
}

/**
 * @param {Record<string, string>} sections
 */
function applyRenderedSections(sections) {
  if (!sections || typeof sections !== "object") {
    return;
  }

  const targets = getSectionTargets();

  for (const target of targets) {
    const sectionHtml = sections[target.section];
    if (!sectionHtml) {
      continue;
    }

    const root = document.getElementById(target.id);
    if (!root) {
      continue;
    }

    if (target.selector) {
      const elementToReplace = root.querySelector(target.selector) || root;
      elementToReplace.innerHTML = getSectionInnerHTML(
        sectionHtml,
        target.selector
      );
    } else {
      root.innerHTML = sectionHtml;
    }
  }
}

/**
 * @param {Record<string, string> | null | undefined} properties
 * @returns {boolean}
 */
function lineHasFreeGiftProperty(properties) {
  if (!properties || typeof properties !== "object") {
    return false;
  }

  return Object.prototype.hasOwnProperty.call(properties, FREE_GIFT_KEY);
}

/**
 * @param {CartJson} cart
 * @returns {number}
 */
function qualifyingSubtotalCents(cart) {
  if (!cart.items?.length) {
    return 0;
  }

  let sum = 0;

  for (const item of cart.items) {
    if (lineHasFreeGiftProperty(item.properties)) {
      continue;
    }

    sum += item.final_line_price ?? 0;
  }

  return sum;
}

/**
 * @param {CartLine} item
 * @returns {boolean}
 */
function lineIsGiftCard(item) {
  if (item?.gift_card === true) {
    return true;
  }

  const productType =
    typeof item?.product_type === "string" ? item.product_type.trim() : "";
  return productType.toLowerCase() === "giftcard";
}

/**
 * @param {CartJson} cart
 * @returns {boolean}
 */
function containsOnlyGiftCardsAndFreeGifts(cart) {
  if (!cart.items?.length) {
    return false;
  }

  return cart.items.every(
    (item) => lineHasFreeGiftProperty(item.properties) || lineIsGiftCard(item)
  );
}

/**
 * @param {CartJson} cart
 * @param {number} variantId
 * @returns {CartLine | undefined}
 */
function findFreeGiftLine(cart, variantId) {
  if (!cart.items?.length) {
    return undefined;
  }

  for (const item of cart.items) {
    if (
      item.variant_id === variantId &&
      lineHasFreeGiftProperty(item.properties)
    ) {
      return item;
    }
  }

  return undefined;
}

/**
 * @param {string} codesRaw
 * @returns {string[]}
 */
function parseCodeTokens(codesRaw) {
  if (!codesRaw || typeof codesRaw !== "string") {
    return [];
  }

  return codesRaw
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.toUpperCase());
}

/**
 * @param {CartJson} cart
 * @returns {string[]}
 */
function applicableDiscountCodes(cart) {
  const list = cart.discount_codes;
  if (!list?.length) {
    return [];
  }

  /** @type {string[]} */
  const out = [];

  for (const d of list) {
    if (d.applicable && typeof d.code === "string") {
      out.push(d.code);
    }
  }

  return out;
}

/**
 * @param {string[]} appliedCodes
 * @param {string[]} tokensUpper
 * @param {'exact' | 'prefix'} mode
 * @returns {string | null}
 */
function findMatchingAppliedCode(appliedCodes, tokensUpper, mode) {
  if (tokensUpper.length === 0) {
    return null;
  }

  for (const token of tokensUpper) {
    for (const applied of appliedCodes) {
      const appliedUpper = applied.toUpperCase();
      if (mode === "prefix") {
        if (appliedUpper.startsWith(token)) {
          return applied;
        }
      } else if (appliedUpper === token) {
        return applied;
      }
    }
  }

  return null;
}

/**
 * @param {number | null} startUnix
 * @param {number | null} endUnix
 * @returns {boolean}
 */
function inDateWindow(startUnix, endUnix) {
  const nowSec = Math.floor(Date.now() / 1000);

  if (startUnix != null && nowSec < startUnix) {
    return false;
  }

  if (endUnix != null && nowSec > endUnix) {
    return false;
  }

  return true;
}

/**
 * @param {CartJson | null | undefined} cart
 */
function syncEmptyStateClasses(cart) {
  const isEmpty = !cart || cart.item_count === 0;
  const drawer = document.querySelector("cart-drawer");
  if (drawer instanceof HTMLElement) {
    drawer.classList.toggle("is-empty", isEmpty);
  }

  const drawerItems = document.querySelector("cart-drawer-items");
  if (drawerItems instanceof HTMLElement) {
    drawerItems.classList.toggle("is-empty", isEmpty);
  }

  const cartItems = document.querySelector("cart-items");
  if (cartItems instanceof HTMLElement) {
    cartItems.classList.toggle("is-empty", isEmpty);
  }

  const cartFooter = document.getElementById("main-cart-footer");
  if (cartFooter instanceof HTMLElement) {
    cartFooter.classList.toggle("is-empty", isEmpty);
  }
}

/**
 * @param {CartJson} cart
 */
function notifyCartUpdated(cart) {
  syncEmptyStateClasses(cart);

  if (typeof window.publish === "function") {
    window.publish(CART_UPDATE_EVENT, {
      source: "gift-rules-sync",
      cartData: cart,
      variantId: null
    });
    return;
  }

  document.dispatchEvent(
    new CustomEvent(CART_UPDATE_EVENT, {
      detail: {
        source: "gift-rules-sync",
        cartData: cart
      }
    })
  );
}

/**
 * @extends {HTMLElement}
 */
class GiftRulesSync extends HTMLElement {
  /** @type {boolean} */
  #syncing = false;

  /** @type {GiftRule[]} */
  #rules = [];

  /** @type {number | null} */
  #queuedSync = null;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  /** @type {number | null} */
  #subscribeRetryTimer = null;

  /** @type {CartJson | null} */
  #lastCartSnapshot = null;

  connectedCallback() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      return;
    }

    const el = document.getElementById(`GiftRulesConfig-${sectionId}`);
    if (!(el instanceof HTMLScriptElement) || el.type !== "application/json") {
      return;
    }

    try {
      const parsed = JSON.parse(el.textContent || "[]");
      this.#rules = Array.isArray(parsed)
        ? /** @type {GiftRule[]} */ (parsed)
        : [];
    } catch {
      this.#rules = [];
    }

    this.#ensureCartSubscription();
    document.addEventListener(CART_UPDATE_EVENT, this.#onCartOrDiscount);
    document.addEventListener("add-to-cart-submit", this.#onCartOrDiscount);

    queueMicrotask(() => {
      this.#sync().catch(() => {});
    });
  }

  disconnectedCallback() {
    if (this.#unsubscribe) {
      this.#unsubscribe();
      this.#unsubscribe = null;
    }
    if (this.#subscribeRetryTimer !== null) {
      window.clearTimeout(this.#subscribeRetryTimer);
      this.#subscribeRetryTimer = null;
    }
    document.removeEventListener(CART_UPDATE_EVENT, this.#onCartOrDiscount);
    document.removeEventListener("add-to-cart-submit", this.#onCartOrDiscount);
    if (this.#queuedSync !== null) {
      window.clearTimeout(this.#queuedSync);
      this.#queuedSync = null;
    }
  }

  #onCartOrDiscount = () => {
    if (this.#syncing || this.#queuedSync !== null) {
      return;
    }

    this.#queuedSync = window.setTimeout(() => {
      this.#queuedSync = null;
      this.#sync().catch(() => {});
    }, 50);
  };

  #ensureCartSubscription() {
    if (this.#unsubscribe) {
      return;
    }

    if (typeof window.subscribe === "function") {
      this.#unsubscribe = window.subscribe(
        CART_UPDATE_EVENT,
        this.#onCartOrDiscount
      );
      return;
    }

    this.#subscribeRetryTimer = window.setTimeout(() => {
      this.#subscribeRetryTimer = null;
      this.#ensureCartSubscription();
    }, 300);
  }

  /**
   * @param {GiftRule} rule
   * @param {CartJson} cart
   * @returns {{ shouldApply: boolean; matchedCode: string | null }}
   */
  #evaluateRule(rule, cart) {
    if (rule.thresholdCents == null) {
      return {
        shouldApply: false,
        matchedCode: null
      };
    }

    if (containsOnlyGiftCardsAndFreeGifts(cart)) {
      return {
        shouldApply: false,
        matchedCode: null
      };
    }

    if (!inDateWindow(rule.startUnix, rule.endUnix)) {
      return {
        shouldApply: false,
        matchedCode: null
      };
    }

    const subtotal = qualifyingSubtotalCents(cart);

    if (subtotal < rule.thresholdCents) {
      return {
        shouldApply: false,
        matchedCode: null
      };
    }

    if (!rule.requiresDiscountCode) {
      return {
        shouldApply: true,
        matchedCode: null
      };
    }

    const applied = applicableDiscountCodes(cart);
    const tokens = parseCodeTokens(rule.codes);
    const mode = rule.matchMode === "prefix" ? "prefix" : "exact";
    const matchedCode = findMatchingAppliedCode(applied, tokens, mode);

    return {
      shouldApply: Boolean(matchedCode),
      matchedCode
    };
  }

  /**
   * @param {number} variantId
   * @returns {string}
   */
  #dismissKey(variantId) {
    return `${DISMISSED_PREFIX}${variantId}`;
  }

  /**
   * @param {CartJson} cart
   */
  #clearDismissWhenIneligible(cart) {
    for (const rule of this.#rules) {
      if (!this.#evaluateRule(rule, cart).shouldApply) {
        window.sessionStorage.removeItem(this.#dismissKey(rule.variantId));
      }
    }
  }

  /**
   * @param {CartJson | null} previous
   * @param {CartJson} current
   */
  #recordManualRemoval(previous, current) {
    if (!previous?.items?.length) {
      return;
    }

    for (const rule of this.#rules) {
      const hadLine = findFreeGiftLine(previous, rule.variantId);
      const hasLine = findFreeGiftLine(current, rule.variantId);
      const shouldHave = this.#evaluateRule(rule, current).shouldApply;

      if (hadLine && !hasLine && shouldHave) {
        window.sessionStorage.setItem(this.#dismissKey(rule.variantId), "1");
      }
    }
  }

  /**
   * @param {number} variantId
   * @returns {boolean}
   */
  #isDismissed(variantId) {
    return window.sessionStorage.getItem(this.#dismissKey(variantId)) === "1";
  }

  /**
   * @param {CartJson} cart
   * @returns {CartJson}
   */
  #cloneCartSnapshot(cart) {
    return /** @type {CartJson} */ (JSON.parse(JSON.stringify(cart)));
  }

  /**
   * @returns {Set<number>}
   */
  #ruleVariantIdSet() {
    const out = new Set();
    for (const rule of this.#rules) {
      if (Number.isFinite(rule.variantId)) {
        out.add(rule.variantId);
      }
    }
    return out;
  }

  /**
   * @param {Set<number>} activeVariantIds
   */
  #clearOrphanDismissKeys(activeVariantIds) {
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (!key || !key.startsWith(DISMISSED_PREFIX)) {
        continue;
      }
      const rawId = key.slice(DISMISSED_PREFIX.length);
      const variantId = Number(rawId);
      if (!Number.isFinite(variantId) || !activeVariantIds.has(variantId)) {
        window.sessionStorage.removeItem(key);
        i -= 1;
      }
    }
  }

  #sync = async () => {
    if (this.#syncing) {
      return;
    }

    this.#syncing = true;

    /** @type {boolean} */
    let mutated = false;

    try {
      const previousSnapshot = this.#lastCartSnapshot;

      let cart = await this.#fetchCart();
      if (!cart) {
        return;
      }

      this.#clearDismissWhenIneligible(cart);
      this.#recordManualRemoval(previousSnapshot, cart);
      const activeVariantIds = this.#ruleVariantIdSet();
      this.#clearOrphanDismissKeys(activeVariantIds);

      for (const item of cart.items) {
        if (!lineHasFreeGiftProperty(item.properties)) {
          continue;
        }
        if (activeVariantIds.has(item.variant_id)) {
          continue;
        }

        const fallbackLine = cart.items.indexOf(item) + 1;
        const lineNumber = Number.isInteger(item.line)
          ? item.line
          : fallbackLine;
        const lineKey = typeof item.key === "string" ? item.key : null;
        await this.#removeLine(lineNumber, lineKey);
        mutated = true;

        const next = await this.#fetchCart();

        if (next) {
          cart = next;
        }
      }

      for (const rule of this.#rules) {
        const evaluation = this.#evaluateRule(rule, cart);
        const shouldHave = evaluation.shouldApply;
        const line = findFreeGiftLine(cart, rule.variantId);

        if (shouldHave && !line) {
          if (this.#isDismissed(rule.variantId)) {
            continue;
          }
          const ok = await this.#addGift(
            rule.variantId,
            evaluation.matchedCode
          );

          if (ok) {
            mutated = true;
          }

          const next = await this.#fetchCart();
          if (next) {
            cart = next;
          }
        } else if (!shouldHave && line) {
          window.sessionStorage.removeItem(this.#dismissKey(rule.variantId));
          const fallbackLine = cart.items.indexOf(line) + 1;
          const lineNumber = Number.isInteger(line.line)
            ? line.line
            : fallbackLine;
          const lineKey = typeof line.key === "string" ? line.key : null;
          await this.#removeLine(lineNumber, lineKey);
          mutated = true;
          const next = await this.#fetchCart();
          if (next) {
            cart = next;
          }
        }
      }
    } finally {
      this.#syncing = false;
    }

    const finalCart = await this.#fetchCart();
    if (finalCart) {
      this.#lastCartSnapshot = this.#cloneCartSnapshot(finalCart);
    }

    if (mutated) {
      if (finalCart) {
        notifyCartUpdated(finalCart);
      }
    }
  };

  /**
   * @returns {Promise<CartJson | null>}
   */
  #fetchCart = async () => {
    try {
      const response = await fetch("/cart.js");
      if (!response.ok) {
        return null;
      }

      return /** @type {CartJson} */ (await response.json());
    } catch {
      return null;
    }
  };

  /**
   * @param {number} variantId
   * @param {string | null} [matchedDiscountCode]
   * @returns {Promise<boolean>}
   */
  #addGift = async (variantId, matchedDiscountCode = null) => {
    const cartRoutes = getCartRoutes();
    if (!cartRoutes) {
      return false;
    }

    const formData = new FormData();
    formData.append("id", String(variantId));
    formData.append("quantity", "1");
    formData.append(`properties[${FREE_GIFT_KEY}]`, FREE_GIFT_VALUE);
    if (
      typeof matchedDiscountCode === "string" &&
      matchedDiscountCode.trim() !== ""
    ) {
      formData.append(`properties[${FREE_GIFT_CODE}]`, matchedDiscountCode);
    }

    const sectionTargets = getSectionTargets();
    const sectionIds = sectionTargets.map((target) => target.section);
    if (sectionIds.length) {
      formData.append("sections", sectionIds.join(","));
      formData.append("sections_url", window.location.pathname);
    }

    const fetchCfg = getFetchConfig("javascript");
    delete fetchCfg.headers["Content-Type"];
    fetchCfg.body = formData;

    const response = await fetch(cartRoutes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "application/json"
      }
    });

    const data = await response.json();

    if (data.status) {
      return false;
    }

    syncEmptyStateClasses(data);
    if (data.sections) {
      applyRenderedSections(data.sections);
    }

    return true;
  };

  /**
   * @param {number} line
   * @param {string | null} lineKey
   */
  #removeLine = async (line, lineKey = null) => {
    const cartRoutes = getCartRoutes();
    if (!cartRoutes) {
      return;
    }

    const sectionTargets = getSectionTargets();
    const sectionsToUpdate = sectionTargets.map((target) => target.section);

    const payload = {
      quantity: 0,
      sections: sectionsToUpdate,
      sections_url: window.location.pathname
    };

    if (lineKey) {
      payload.id = lineKey;
    } else {
      payload.line = line;
    }

    const body = JSON.stringify(payload);

    const response = await fetch(cartRoutes.cart_change_url, {
      ...getFetchConfig("json"),
      body
    });
    const data = await response.json();
    syncEmptyStateClasses(data);
    if (data && data.sections) {
      applyRenderedSections(data.sections);
    }
  };
}

if (!customElements.get("gift-rules-sync")) {
  customElements.define("gift-rules-sync", GiftRulesSync);
}
